import { FC, ReactNode } from "react";
import { Stack } from "../../layout";
import { Semibold, Body } from "../../typography";
import { BaseProps } from "../../BaseProps";
import { createComponentStyleHook } from "../../tokens";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import tokenStyles from "./styles.module.css";
import legacyStyles from "./styles.module.scss";

const useResolvedInputAlignmentContainerStyles = createComponentStyleHook<InputAlignmentContainerProps>({
  legacyStyles,
  tokenStyles,
});

/**
 * The `InputAlignmentContainer` properties.
 */
export interface InputAlignmentContainerProps extends BaseProps {
  /**
   * The set of child components.
   */
  readonly children?: ReactNode;
}

/**
 * A layout component that aligns children to form inputs by padding the top of the container
 * with the same metrics as an input label.
 *
 * This component is purely presentational and does not add any semantic meaning or
 * interactive behavior. It maintains accessibility by preserving the natural document
 * structure and not interfering with assistive technology.
 *
 * `import { InputAlignmentContainer } from "./InputAlignmentContainer";`
 *
 * <strong>Example use case:</strong>
 *
 * ```typescript
 * <InputAlignmentContainer>
 *   <Button>Aligned Button</Button>
 * </InputAlignmentContainer>
 * ```
 */
export const InputAlignmentContainer: FC<InputAlignmentContainerProps> = (props) => {
  const { children } = props;
  const { mode, styles, StyleModeBoundary, isLegacy } = useResolvedInputAlignmentContainerStyles(props);
  const basePropsForHtml = isLegacy ? props : { ...props, className: undefined, style: undefined };
  const baseHtmlProps = getHtmlPropsFromBaseProps(basePropsForHtml, {
    className: [styles.InputAlignmentContainer, "input-alignment-container"],
  });

  return (
    <StyleModeBoundary mode={mode}>
      <div {...baseHtmlProps}>
        <Stack spacing="mini" alignment="span">
          <Body inline size="mini">
            <Semibold>
              <span aria-hidden="true">&nbsp;</span>
            </Semibold>
          </Body>
          <Stack alignment="span">
            <Stack.Spacer />
            {children}
            <Stack.Spacer />
          </Stack>
        </Stack>
      </div>
    </StyleModeBoundary>
  );
};
